/**
 * One row of the chat thread (de-slop CHAT-6 / CHAT-16).
 *
 * A memoized component keyed by message id: the FlatList no longer
 * re-renders every row when unrelated screen state changes, and the
 * streaming bubble keeps its MarkdownRenderer while its text grows.
 */
import React, { memo } from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system';
import MarkdownRenderer from '../../../components/MarkdownRenderer';
import { ReportCard } from '../../../components/report/ReportCard';
import { useTranslation } from '../../../hooks/useTranslation';
import type { ChatMessage } from '../../../types';
import {
  COLORS,
  WAITING_BUBBLE_ID,
  getVisibleUserMessageContent,
} from '../chatConstants';
import { formatDateTime } from '../chatFormat';
import { brand } from '../../../design-system/theme/tokens';

const selectableTextStyle = {
  userSelect: 'text',
  WebkitUserSelect: 'text',
  cursor: 'text',
} as any;

export interface ChatMessageRowProps {
  item: ChatMessage;
  isPhone: boolean;
  /** Translated tip shown in the "thinking" bubble. */
  waitingTip: string;
  /** Show "Download PDF" (only on the analysis output message). */
  showPdfButton: boolean;
  onExportPdf: () => void;
  /** Report lane (D0): the analysis output links to the canonical report screen. */
  reportSessionId?: number | null;
}

function ChatMessageRowImpl({ item, isPhone, waitingTip, showPdfButton, onExportPdf, reportSessionId = null }: ChatMessageRowProps) {
  const { t } = useTranslation();
  const isAssistantMessage = item.role === 'assistant';

  if (item.id === WAITING_BUBBLE_ID) {
    return (
      <View style={{ marginBottom: 16, alignItems: 'flex-start' }}>
        <View style={{
          maxWidth: isPhone ? '100%' : '76%',
          minWidth: 0,
          borderRadius: 16,
          padding: 16,
          backgroundColor: '#DBEAFE', // Soft blue background
          borderLeftWidth: 4,
          borderLeftColor: brand.primary, // Primary blue accent
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.1,
          shadowRadius: 2,
          elevation: 2,
        }}>
          {/* Thinking indicator */}
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
            <ActivityIndicator size="small" color={brand.primary} style={{ marginRight: 8 }} />
            <Text style={{ fontSize: 13, fontWeight: '600', color: '#1E3A8A' }}>
              {t('chat.aiThinking')}
            </Text>
          </View>

          {/* Tip content */}
          <View style={{
            backgroundColor: 'rgba(30, 64, 175, 0.1)',
            borderRadius: 8,
            padding: 12,
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
              <Text style={{ fontSize: 16, marginRight: 8 }}>💡</Text>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 11, fontWeight: '600', color: brand.primary, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('chat.tip')}
                </Text>
                <Text style={{ fontSize: 13, lineHeight: 18, color: '#1E3A8A' }}>
                  {waitingTip}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={{
      width: isAssistantMessage ? '100%' : undefined,
      marginBottom: 18,
      alignItems: item.role === 'user' ? 'flex-end' : 'flex-start',
    }}>
      <View style={{
        width: isAssistantMessage ? '100%' : undefined,
        maxWidth: isAssistantMessage ? '100%' : isPhone ? '88%' : '72%',
        minWidth: 0,
        borderRadius: isAssistantMessage ? 14 : 16,
        padding: isAssistantMessage ? (isPhone ? 14 : 18) : 16,
        backgroundColor: item.role === 'user' ? COLORS.primary : reviewDesk.card,
        borderWidth: isAssistantMessage ? 1 : 0,
        borderColor: reviewDesk.line,
        borderBottomRightRadius: item.role === 'user' ? 4 : 16,
        borderBottomLeftRadius: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: isAssistantMessage ? 0.04 : 0.1,
        shadowRadius: isAssistantMessage ? 6 : 2,
        elevation: isAssistantMessage ? 0 : 2,
        overflow: isAssistantMessage ? 'hidden' : 'visible',
      }}>
        {reportSessionId ? <ReportCard sessionId={reportSessionId} /> : null}
        {isAssistantMessage ? (
          <MarkdownRenderer
            content={item.content}
            style={{ color: COLORS.text.primary }}
            isUserMessage={false}
          />
        ) : (
          <Text
            selectable
            selectionColor="rgba(255, 255, 255, 0.35)"
            style={[
              { fontSize: 15, lineHeight: 22, color: 'white' },
              selectableTextStyle,
            ]}
          >
            {getVisibleUserMessageContent(item.content)}
          </Text>
        )}

        {/* PDF Download — shown only on the analysis output itself, not on
            general chat replies. */}
        {showPdfButton && (
          <TouchableOpacity
            onPress={onExportPdf}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              alignSelf: 'flex-start',
              marginTop: 10,
              paddingVertical: 6,
              paddingHorizontal: 12,
              borderRadius: 8,
              backgroundColor: 'rgba(30, 64, 175, 0.08)',
            }}
            accessibilityLabel={t('chat.downloadPdf')}
            accessibilityRole="button"
          >
            <MaterialIcons name="picture-as-pdf" size={16} color={brand.primary} />
            <Text style={{
              fontSize: 12,
              fontWeight: '600',
              color: brand.primary,
              marginLeft: 6,
            }}>
              {t('chat.downloadPdf')}
            </Text>
          </TouchableOpacity>
        )}

        {item.sources && Array.isArray(item.sources) && item.sources.length > 0 && (
          <View style={{
            marginTop: 12,
            paddingTop: 12,
            borderTopWidth: 1,
            borderTopColor: 'rgba(0,0,0,0.1)'
          }}>
            <Text style={{
              fontSize: 11,
              fontWeight: '600',
              color: COLORS.text.secondary,
              marginBottom: 6
            }}>
              {t('chat.sources')}:
            </Text>
            {item.sources.map((source: any, index: number) => {
              const filename = typeof source === 'string' ? source : (source?.filename || t('common.unknown'));
              const score = typeof source === 'object' && typeof source?.score === 'number' ? source.score : 0;

              return (
                <Text key={index} style={{
                  fontSize: 11,
                  color: COLORS.text.muted,
                  marginBottom: 2
                }}>
                  • {filename} {score > 0 && `(${(score * 100).toFixed(1)}%)`}
                </Text>
              );
            })}
          </View>
        )}

        <Text style={{
          fontSize: 11,
          marginTop: 8,
          color: item.role === 'user' ? 'rgba(255,255,255,0.7)' : COLORS.text.muted
        }}>
          {formatDateTime(item.created_at)}
        </Text>
      </View>
    </View>
  );
}

export const ChatMessageRow = memo(ChatMessageRowImpl);
